import { get, writable } from "svelte/store";
import { INTERVIEWS } from "../util/constants";
import * as local from "./local.js";

let master, analyser;

const ua = navigator.userAgent.toLowerCase();
let hasChrome = ua.includes("chrome");
let hasSafari = ua.includes("safari");
let isIOS =
  (ua.indexOf("iphone") >= 0 && ua.indexOf("like iphone") < 0) ||
  (ua.indexOf("ipad") >= 0 && ua.indexOf("like ipad") < 0) ||
  (ua.indexOf("ipod") >= 0 && ua.indexOf("like ipod") < 0) ||
  (ua.indexOf("mac os x") >= 0 && navigator.maxTouchPoints > 0); // New ipads show up as macs in user agent, but they have a touch screen

let canUseWebAudio = true;
if (isIOS || (hasSafari && !hasChrome)) {
  canUseWebAudio = false;
}
// let safariVersionMatch = ua.match(/version\/([\d+\.]+)/i);
// if ((isIOS || hasSafari) && safariVersionMatch) {
// const vers = parseFloat(safariVersionMatch[1]);
// if (vers < 16.4) canUseWebAudio = false;
// alert(vers);
// }

export const USE_WEBAUDIO = canUseWebAudio;
// export const USE_NATIVE_AUDIO = false; // !canUseWebAudio;

console.log(
  USE_WEBAUDIO ? "WebAudio enabled" : "WebAudio disabled, using fallback"
);

let audioContext;
getGlobalContext();

let freqBins;

const audioBands = [
  { low: 0, high: 50 },
  { low: 50, high: 200 },
  { low: 200, high: 300 },
  { low: 300, high: 2500 },
  { low: 2500, high: 7500 },
];

export const PLAYER_CACHE = new Map();

export const activePlayer = writable(null);

export function stopAllExcept(player) {
  for (let other of PLAYER_CACHE.values()) {
    if (other !== player) {
      if (other.playing) {
        if (other.loading) {
          other.load().then(() => stop());
        } else {
          other.stop();
        }
      }
    }
  }
}

export function getPlayerSingleton(id) {
  let player;
  if (!PLAYER_CACHE.has(id)) {
    player = createAudioPlayer(INTERVIEWS.find((f) => f.id === id));
    PLAYER_CACHE.set(id, player);
    // console.log("creating", id);
  } else {
    player = PLAYER_CACHE.get(id);
    // console.log("getting cache", id); //
  }
  return player;
}

export async function resume() {
  if (!USE_WEBAUDIO) return;
  const audioContext = getGlobalContext();
  if (audioContext.state == "suspended") {
    return audioContext.resume();
  }
  return Promise.resolve();
}

export function isAuduioTagPlaying(audio) {
  return (
    audio &&
    audio.currentTime > 0 &&
    !audio.paused &&
    !audio.ended &&
    audio.readyState > 2
  );
}

export function computeAnalyserSignal(player, time = 0) {
  if (!analyser || !player) {
    const playing = player && isAuduioTagPlaying(player.audio);
    return audioBands.map((_, i) => {
      const ut = time / 1000;
      const t = i / (audioBands.length - 1);

      const f0 = Math.sin(Math.PI * 0.25 + t * Math.PI + ut * Math.PI * 2);
      const f1 =
        Math.sin(Math.PI * 0.75 + t * Math.PI * 2 + ut * Math.PI * 1) * 0.5;
      const f2 =
        Math.sin(Math.PI * 0.5 + t * Math.PI * 2 + ut * Math.PI * 2) * 1;
      return (
        (Math.sin(f0 + f1 + f2 + ut * 1) * 0.5 + 0.5) * 0.75 * (playing ? 1 : 0)
      );
    });
  }
  analyser.getByteFrequencyData(freqBins);
  return audioBands.map(({ low, high }) => {
    return analyserFrequencyAverage(0xff, analyser, freqBins, low, high);
  });
}

function frequencyToIndex(frequency, sampleRate, frequencyBinCount) {
  var nyquist = sampleRate / 2;
  var index = Math.round((frequency / nyquist) * frequencyBinCount);
  return Math.max(0, Math.min(frequencyBinCount, index));
}

function analyserFrequencyAverage(div, analyser, frequencies, minHz, maxHz) {
  var sampleRate = analyser.context.sampleRate;
  var binCount = analyser.frequencyBinCount;
  var start = frequencyToIndex(minHz, sampleRate, binCount);
  var end = frequencyToIndex(maxHz, sampleRate, binCount);
  var count = end - start;
  var sum = 0;
  for (; start < end; start++) {
    sum += frequencies[start] / div;
  }
  return count === 0 ? 0 : sum / count;
}

export function getMaster() {
  if (!USE_WEBAUDIO) return null;
  if (!master) {
    const audioContext = getGlobalContext();

    analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.75;
    freqBins = new Uint8Array(analyser.fftSize);

    master = audioContext.createGain();
    master.connect(audioContext.destination);
    analyser.connect(master);
  }
  return analyser;
}

export function getGlobalContext() {
  if (!USE_WEBAUDIO) return null;
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

export function createAudioPlayer(meta = {}) {
  const { id } = meta;
  const audio = document.createElement("audio");
  audio.crossOrigin = "anonymous";
  audio.setAttribute("controls", "controls");
  audio.setAttribute("preload", "metadata");
  // if (USE_NATIVE_AUDIO) {
  //   audio.setAttribute("controls", "controls");
  //   audio.setAttribute("preload", "metadata");
  // }
  // if (!USE_NATIVE_AUDIO)
  document.body.appendChild(audio);
  audio.classList.add("noselect");
  audio.style.cssText = `position: fixed; z-index:100000000; top: 0; left: 0; pointer-events: none; display: none;`;

  let loaded = false;
  let loading = false;
  let loadPromise;
  // let interval;
  let currentTime = local.get(`audio-${id}:currentTime`, 0);
  let playing = false;

  let source, gain;
  let state, data;

  // initSource();

  window.addEventListener(
    "beforeunload",
    () => {
      storeTime();
    },
    { passive: true }
  );

  const seekToState = () => {
    if (Math.abs(audio.currentTime - currentTime) > 0.1) {
      // console.log("do seek");
      audio.currentTime = currentTime;
    }
  };

  const updatePlay = () => {
    // initSource();
    seekToState();
    if (!playing) {
      playing = true;
      storeTime();
      state.update((d) => d);
    }
  };

  const updateMedia = () => {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: meta.title,
        artist: "Bitframes",
        album: meta.speakers,
        artwork: [
          {
            src: "https://r2.bitframes.io/thumb.jpg",
            sizes: "512x512",
            type: "image/jpeg",
          },
        ],
      });
      navigator.mediaSession.setActionHandler("play", () => audio.play());
      navigator.mediaSession.setActionHandler("pause", () => audio.pause());
      navigator.mediaSession.setActionHandler("previoustrack", () =>
        seekRelative(-15)
      );
      navigator.mediaSession.setActionHandler("nexttrack", () =>
        seekRelative(30)
      );
      navigator.mediaSession.setActionHandler("seekbackward", () =>
        seekRelative(-15)
      );
      navigator.mediaSession.setActionHandler("seekforward", () =>
        seekRelative(30)
      );
      navigator.mediaSession.setActionHandler("seekto", (details) => {
        if (details.seekTime) {
          audio.currentTime = details.seekTime;
        }
      });
    }
  };

  audio.addEventListener("playing", () => {
    // console.log("playing");
    navigator.mediaSession.playbackState = "playing";
    window.audio = audio;
    updatePlay();
    updateMedia();
  });

  audio.addEventListener("play", () => {
    // console.log("play");
    stopAllExcept(data);
    updatePlay();
    activePlayer.update(() => data);
  });

  audio.addEventListener("ended", () => {
    playing = false;
  });
  audio.addEventListener("pause", () => {
    // console.log("media element pause");
    navigator.mediaSession.playbackState = "paused";
    playing = false;
    state.update((d) => d);
  });

  audio.addEventListener("timeupdate", () => {
    storeTime();
  });

  audio.addEventListener("loadedmetadata", () => {
    updateMedia();
    seekToState();
  });

  audio.addEventListener(
    "click",
    () => {
      trigger();
    },
    { once: true }
  );

  data = {
    id,
    meta,
    audio,
    seekRelative,
    seek(u, storage = true) {
      if (!isFinite(u)) return;
      u = Math.max(0, Math.min(1, u));
      const duration = this.getDuration();
      const seconds = u * duration;
      audio.currentTime = seconds;
      if (storage) storeTime();
    },
    getDuration() {
      return isFinite(audio.duration) && audio.duration != null
        ? audio.duration
        : meta.duration;
    },
    get ready() {
      return isFinite(audio.duration) && isFinite(audio.currentTime);
    },
    get state() {
      return state;
    },
    get duration() {
      return audio.duration;
    },
    get loading() {
      return loading;
    },
    get loaded() {
      return loaded;
    },
    get currentTime() {
      return currentTime;
    },
    get playing() {
      return playing;
    },
    audio,
    play,
    stop,
    load,
    toggle,
    trigger,
  };

  state = writable(data);

  updateMedia();

  return data;

  function seekRelative(sec, storage = true) {
    const time = audio.currentTime || 0;
    const newTime = Math.max(0, Math.min(meta.duration, time + sec));
    audio.currentTime = newTime;
    if (storage) storeTime();
  }

  function initSource() {
    if (source || gain) return;
    if (!USE_WEBAUDIO) return;
    const audioContext = getGlobalContext();
    source = audioContext.createMediaElementSource(audio);
    gain = audioContext.createGain();
    source.connect(gain);
    gain.connect(getMaster());
    return resume();
  }

  function trigger() {
    return initSource();
  }

  async function toggle() {
    if (playing) {
      stop();
      return Promise.resolve();
    } else {
      const promises = [load()];
      play();
      return Promise.all(promises);
    }
  }

  function stop() {
    storeTime();
    audio.pause();
  }

  function storeTime() {
    if (audio.currentTime) {
      currentTime = audio.currentTime || 0;
      local.set(`audio-${id}:currentTime`, currentTime);
      state.update((d) => d);
    }
  }

  function play() {
    audio.play();
  }

  async function load() {
    if (loadPromise) {
      return loadPromise;
    } else {
      loadPromise = new Promise((resolve) => {
        const source = document.createElement("source");
        // if (audio.canPlayType("audio/mpeg")) {
        source.src = `${meta.src}.mp3`;
        source.type = "audio/mpeg";
        // } else {
        //   source.src = `${meta.src}.ogg`;
        //   source.type = "audio/ogg";
        // }
        audio.addEventListener(
          "loadedmetadata",
          () => {
            console.log(id, audio.duration);
            resolve();
            loaded = true;
            loading = false;
            // console.log("Audio can play");
            state.update((d) => d);
          },
          { once: true }
        );
        audio.appendChild(source);
        loading = true;
        state.update((d) => d);
      });
      return loadPromise;
    }
  }
}

// export const currentPlaying = writable(null);

// const audioPlayers = INTERVIEWS.map((meta) => {
//   const { id } = meta;
//   const audio = document.createElement("audio");
//   let loaded = false;
//   let loading = false;
//   let loadPromise;
//   let interval;
//   let currentTime = local.get(`audio-${id}:currentTime`, 0);
//   let playing = false;
//   let elementPlaying = false;

//   window.addEventListener(
//     "beforeunload",
//     () => {
//       storeTime();
//     },
//     { passive: true }
//   );

//   audio.addEventListener("play", () => {
//     console.log("playing");
//     elementPlaying = true;
//   });

//   audio.addEventListener("pause", () => {
//     console.log("paused");
//     elementPlaying = false;
//   });

//   return {
//     ...meta,
//     get loading() {
//       return loading;
//     },
//     get loaded() {
//       return loaded;
//     },
//     get currentTime() {
//       return currentTime;
//     },
//     get isPlaying() {
//       return playing;
//     },
//     audio,
//     play,
//     stop,
//     load,
//   };

//   function stop() {
//     storeTime();
//     playing = false;
//     audio.pause();
//     currentPlaying.update((d) => d);
//   }

//   function storeTime() {
//     if (audio.currentTime) {
//       currentTime = audio.currentTime || 0;
//       local.set(`audio-${id}:currentTime`, currentTime);
//     }
//   }

//   function play() {
//     if (playing) return;
//     audio.currentTime = currentTime;
//     audio.play();
//     playing = true;
//     clearInterval(interval);
//     interval = setInterval(() => {
//       storeTime();
//     }, 1000);
//     currentPlaying.update((d) => d);
//   }

//   async function load() {
//     if (loadPromise) {
//       return loadPromise;
//     } else {
//       loadPromise = new Promise((resolve) => {
//         loading = true;
//         const source = document.createElement("source");
//         if (audio.canPlayType("audio/ogg")) {
//           source.src = `${meta.src}.ogg`;
//           source.type = "audio/ogg";
//         } else {
//           source.src = `${meta.src}.mp3`;
//           source.type = "audio/mpeg";
//         }
//         audio.addEventListener(
//           "canplay",
//           (ev) => {
//             resolve();
//             loaded = true;
//             loading = false;
//             console.log("Audio can play");
//           },
//           { once: true }
//         );
//         audio.appendChild(source);
//       });
//       return loadPromise;
//     }
//   }
// });

// export function getPlayer(id) {
//   return audioPlayers.find((e) => e.id == id);
// }

// export async function play(id) {
//   const player = audioPlayers.find((e) => e.id == id);
//   currentPlaying.update((curPlayer) => {
//     if (curPlayer) {
//       curPlayer.stop();
//     }
//     curPlayer = player;
//     return curPlayer;
//   });
//   await player.load();
//   player.play();
// }

// export async function toggle(id) {
//   const player = audioPlayers.find((e) => e.id == id);
//   if (player && player.isPlaying) {
//     player.stop();
//   } else {
//     return play(id);
//   }
// }

// export async function toggleCurrent() {
//   const player = get(currentPlaying);
//   if (player) {
//     if (player.isPlaying) player.stop();
//     else player.play();
//   }
// }
